import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { useUserManagement } from "hooks";
import { OLITASK_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const API_BASE_URL = `${OLITASK_SERVICE_BASE_URL}/api/v1/credit-applications`;

export default function useCreditApplications() {
  const authToken = sessionStorage.getItem("authToken");
  const {
    users,
    getUserById,
    loading: usersLoading,
    refetch: refetchUsers,
  } = useUserManagement();
  const [creditApplications, setCreditApplications] = useState([]);
  const [selectedCreditApp, setSelectedCreditApp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // handle axios errors
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    setError(message);
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  const fetchCreditApplications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const firstResponse = await apiClient.get(
        `${API_BASE_URL}?page=0&size=${50}`
      );
      const { content: firstContent = [], totalPages = 1 } = firstResponse.data;
      let allCreditApplications = [...firstContent];

      if (totalPages > 1) {
        const promises = [];
        for (let p = 1; p < totalPages; p++) {
          promises.push(
            apiClient.get(`${API_BASE_URL}?page=${p}&size=${50}`)
          );
        }

        const responses = await Promise.all(promises);
        responses.forEach((res) => {
          allCreditApplications = allCreditApplications.concat(
            res.data.content || []
          );
        });
      }

      setCreditApplications(allCreditApplications);
    } catch (err) {
      handleAxiosErrors(err, "Fetch credit applications");
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  // Fetch credit application by ID
  const fetchCreditApplicationById = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get(`${API_BASE_URL}/${id}`);

        const app = res.data;

        // Fetch user details using the userId
        let userDetails = null;
        if (app.userId) {
          try {
            userDetails = await getUserById(app.userId);
          } catch (err) {
            console.warn(`Failed to fetch user ${app.userId}:`, err);
          }
        }

        // Combine credit application with user details
        const fullApp = {
          ...app,
          user: userDetails || null,
        };

        setSelectedCreditApp(fullApp);
        return fullApp;
      } catch (err) {
        handleAxiosErrors(err, "fetch credit application details");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // add credit action e.g approve
  const addCreditApplicationAction = useCallback(
    async (id, payload) => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.post(
          `${API_BASE_URL}/${id}/actions`,
          payload
        );

        const { type } = res.data;
        if(type === "MORE_INFO"){
          toast.success(`Request sent successfully`, {
            theme: "colored",
          });
        }else{
          toast.success(`Application ${type.toLowerCase()}d successfully`, {
            theme: "colored",
          });
        }

        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "add credit application action");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // issue credit action
  const issueApplicationCreditAction = useCallback(
    async (id, payload) => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.post(
          `${API_BASE_URL}/${id}/actions/issue-credit`,
          payload
        );

        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "issue credit application action");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // Delete credit application
  const deleteCreditApplication = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        await apiClient.delete(`${API_BASE_URL}/${id}`);

        // Remove deleted credit application from local state
        setCreditApplications((prev) => prev.filter((key) => key.id !== id));
        toast.success("Credit application deleted successfully!", {
          theme: "colored",
        });
      } catch (err) {
        handleAxiosErrors(err, "Delete credit application");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  // fetch automatically on mount
  useEffect(() => {
    if (authToken) fetchCreditApplications();
  }, [authToken, fetchCreditApplications]);

  return {
    creditApplications,
    selectedCreditApp,
    loading,
    usersLoading,
    error,
    fetchCreditApplicationById,
    deleteCreditApplication,
    setSelectedCreditApp,
    addCreditApplicationAction,
    issueApplicationCreditAction,
  };
}
